import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetHeaderDescription,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { NumericSpinnerInput } from '@/components/inputs/NumericSpinnerInput'
import { Button } from '@/components/Button'
import cx from 'classnames'
import styles from './BookkeepingCompleteCycleDialog.module.css'

export interface BookkeepingCompleteCycleDialogProps {
  showing: boolean
  onClose: () => unknown
  onSubmit: () => unknown
  isSubmitPending: boolean
  itemCount: number
  children?: ReactNode
}

export function BookkeepingCompleteCycleDialog({
  showing,
  onClose,
  onSubmit,
  isSubmitPending,
  itemCount,
  children,
}: BookkeepingCompleteCycleDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader shellSize="sm" withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetTitle large>Mark completed service items</ModalSheetTitle>
          <ModalSheetHeaderDescription>
            Review and adjust the amount completed during this cycle.
          </ModalSheetHeaderDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock />
      </ModalSheetContent>
      <ModalSheetContent>
        <WorkItemsHeaders count={itemCount} />
        {children}
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            isDisabled={isSubmitPending}
          />
          <Button
            label="Complete Cycle"
            variant="primary"
            testId="complete-cycle-submit-button"
            onPress={onSubmit}
            isPending={isSubmitPending}
            isDisabled={isSubmitPending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

interface WorkItemsHeaderProps {
  count: number
}

function WorkItemsHeaders({ count }: WorkItemsHeaderProps) {
  return (
    <div className={styles.workItemsHeadersContainer}>
      <div className={cx(styles.workItemHeader, styles.service)}>
        Services Items <span>({count})</span>
      </div>
      <div className={cx(styles.workItemHeader, styles.remaining)}>
        Remaining
      </div>
      <div className={cx(styles.workItemHeader, styles.completed)}>
        Completed this cycle
      </div>
    </div>
  )
}

export interface BookkeepingCompleteCycleDialogWorkItemRowProps {
  name: string
  description: string
  remaining: number
  min: number
  completedItems: number
  isInvalid: boolean
  onCompletedItemsChange: (value: number | null) => unknown
}

export function BookkeepingCompleteCycleDialogWorkItemRow({
  name,
  description,
  remaining,
  min,
  completedItems,
  isInvalid,
  onCompletedItemsChange,
}: BookkeepingCompleteCycleDialogWorkItemRowProps) {
  return (
    <div className={styles.workItemRow}>
      <div
        className={cx(
          styles.workItemCell,
          styles.service,
          styles.serviceContainer,
        )}
      >
        <div className={styles.serviceName}>{name}</div>
        <div className={styles.serviceDescription}>{description}</div>
      </div>
      <div className={cx(styles.workItemCell, styles.remaining)}>
        {remaining}
      </div>
      <div className={cx(styles.workItemCell, styles.completed)}>
        <NumericSpinnerInput
          min={min}
          max={remaining}
          value={completedItems}
          invalid={isInvalid}
          onChange={onCompletedItemsChange}
          testId={`complete-cycle-work-item-input-${name}`}
        />
      </div>
    </div>
  )
}
